import { createElement } from '../../../core/dom/helpers.js';
import { createLogger } from '../../../core/logging/logger.js';
import { buildAcceptMatcher, normalizeForAccept } from '../../../core/files/accept-matcher.js';
import { deriveFileKind, deriveExtension } from '../../../core/media/file-metadata.js';
import { attachImageFallback } from '../../../core/media/image-fallback.js';
import { createFileDriverSession } from './file-driver-session.js';
import { createImageCard } from './image-card.js';

const log = createLogger({ module: 'file-driver' });

/**
 * @typedef {import('../../../contracts/upload.js').UploadValue} UploadValue
 * @typedef {import('../../../contracts/upload.js').ExistingUpload} ExistingUpload
 * @typedef {import('../../../contracts/upload.js').NewUpload} NewUpload
 * @typedef {import('../../../contracts/file-selection.js').FileMeta} FileMeta
 */

/** Monotonic client-key source for photo cards (keys are never reused). */
let cardKeySeq = 0;
function nextCardKey() { return `pc-${++cardKeySeq}`; }

/**
 * FileMeta → ExistingUpload, defensively. Returns null when there's no url. Carries the library item's
 * `mimeType`/`kind`(→`fileKind`)/`extension` so a photo card can detect images reliably even when the
 * display name lacks an extension. (`id` is intentionally not stored — the deferred file_connections seam;
 * `title`/`description` start empty and the user fills them in on the card.)
 * @param {FileMeta} item
 * @returns {ExistingUpload | null}
 */
function mapFileMetaToExistingUpload(item) {
  if (!item || !item.url) return null;
  return {
    kind: 'existing',
    url: item.url,
    name: item.name || item.url,
    ...(item.mimeType ? { mimeType: item.mimeType } : {}),
    ...(item.kind ? { fileKind: item.kind } : {}),
    ...(item.extension ? { extension: item.extension } : {}),
  };
}

/**
 * UploadResult + source File → ExistingUpload, carrying the same image-detection signals
 * (`mimeType`/`fileKind`/`extension`) the library-item mapper does, so a freshly UPLOADED
 * image renders as a thumb in a photo card even when its name/URL lacks an extension. Derives
 * with the same extension-first, MIME-fallback rule as `createFileMetadataFromUpload` (and the
 * SAME `file.type || result.mimeType` precedence so an empty File.type still classifies).
 * @param {import('../../../contracts/upload.js').UploadResult} result
 * @param {File} file
 * @returns {ExistingUpload}
 */
function mapUploadResultToExistingUpload(result, file) {
  const name = result.name ?? file.name;
  const mimeType = file.type || result.mimeType || '';
  const fileKind = deriveFileKind(name, mimeType);
  const extension = deriveExtension(name);
  return {
    kind: 'existing',
    url: result.url,
    name,
    ...(mimeType ? { mimeType } : {}),
    ...(fileKind ? { fileKind } : {}),
    ...(extension ? { extension } : {}),
  };
}

// ---------------------------------------------------------------------------
// Single-file mode
// ---------------------------------------------------------------------------

/**
 * @param {HTMLElement} container
 * @param {import('../../../contracts/forms.js').FileFieldDef} def
 * @param {import('../../../contracts/forms.js').FieldDriverContext} ctx
 * @returns {import('../../../contracts/forms.js').FieldDriverHandle}
 */
function mountSingle(container, def, ctx) {
  const { onChange } = ctx;
  const accept = def.accept ?? '';
  const acceptMatcher = buildAcceptMatcher(accept);
  const photoCards = def.photo === true; // render the image-card metadata UI
  /** @type {UploadValue | null} */
  let currentValue = ctx.value != null ? /** @type {UploadValue} */ (ctx.value) : null;
  /** @type {string | null} */
  let currentObjectUrl = null;
  /** Photo-card mode: the live card handle (owns its own object URL) + its stable key. */
  /** @type {ReturnType<typeof createImageCard> | null} */
  let cardHandle = null;
  let currentKey = currentValue ? nextCardKey() : '';

  const wrapper = createElement('div', { class: 'file-driver file-driver--single' });

  const preview = createElement('div', { class: 'file-driver__preview' });
  const input = /** @type {HTMLInputElement} */ (createElement('input', {
    type: 'file',
    class: 'file-driver__input',
    id: `field-${def.key}`,
  }));
  if (def.accept) input.setAttribute('accept', def.accept);
  if (def.disabled) input.setAttribute('disabled', 'disabled');

  const hasPicker = Boolean(ctx.filePicker?.browse && ctx.filePicker?.open);
  const actions = createElement('div', { class: 'file-driver__actions' });
  // Library picker (primary) — only when the in-editor file-picker capability is
  // available in ctx.
  const libraryBtn = hasPicker
    ? createElement('button', { type: 'button', class: 'file-driver__library btn btn-primary' })
    : null;
  if (libraryBtn) libraryBtn.textContent = 'Add file';
  // Native disk upload. The modal already exposes its own Upload control, so when
  // the picker is wired this button is redundant — show it ONLY as the fallback
  // for fields without a picker. (Always created so the <input>/change handler
  // wiring below is uniform; just not appended when a picker is present.)
  const chooseBtn = createElement('button', { type: 'button', class: 'file-driver__choose btn' });
  chooseBtn.textContent = 'Upload from computer';

  if (libraryBtn) actions.appendChild(libraryBtn);
  if (!hasPicker) actions.appendChild(chooseBtn);

  // Shared session/abort/error machinery (see file-driver-session.js). The mount keeps
  // its own DOM/preview/value/commit; the session owns busy state, freshness, the
  // picker open dance, and the native-upload lifecycle. `onDisabledChange` fans the
  // disabled state into the dynamic photo card (which isn't in `buttons`).
  const session = createFileDriverSession({
    wrapper, def, ctx, buttons: [input, chooseBtn, libraryBtn],
    onDisabledChange: (disabled) => { cardHandle?.setDisabled(disabled); },
  });
  const { errorEl, showError: showDriverError, clearError: clearDriverError } = session;

  wrapper.appendChild(preview);
  wrapper.appendChild(input);
  wrapper.appendChild(actions);
  wrapper.appendChild(errorEl); // appended last — same DOM order as before
  container.appendChild(wrapper);

  async function openLibraryPicker() {
    const result = await session.openPicker({ multiple: false });
    const mapped = result && result.length ? mapFileMetaToExistingUpload(result[0]) : null;
    if (!mapped) return; // cancel / abort / stale / unusable result
    commitValue(mapped);
  }
  if (libraryBtn) libraryBtn.addEventListener('click', openLibraryPicker);

  // Commit a freshly chosen/uploaded value: a NEW value gets a NEW card key (fresh card
  // identity), then render + onChange. (Title/desc EDITS keep the same key — they patch
  // currentValue in place via the card callbacks, NOT through here.)
  /** @param {UploadValue} val */
  function commitValue(val) {
    currentValue = val;
    currentKey = nextCardKey();
    clearDriverError();
    renderPreview(currentValue);
    onChange(currentValue);
  }

  /** @param {UploadValue | null} val */
  function clearTo(val) {
    currentValue = val;
    currentKey = '';
    renderPreview(currentValue);
    input.value = '';
    clearDriverError();
    onChange(currentValue);
  }

  /** @param {UploadValue | null} val */
  function renderPreview(val) {
    // Tear down any prior object URL / card handle before re-rendering.
    if (currentObjectUrl) {
      URL.revokeObjectURL(currentObjectUrl);
      currentObjectUrl = null;
    }
    if (cardHandle) { cardHandle.destroy(); cardHandle = null; }
    preview.textContent = '';
    if (!val) return;

    // Photo-card mode: render the metadata card CONSISTENTLY (thumb degrades to a
    // filename chip inside the card if the item isn't a recognizable image, but the
    // Title/Description editor always shows).
    if (photoCards) {
      if (!currentKey) currentKey = nextCardKey();
      cardHandle = createImageCard({
        key: currentKey,
        value: val,
        draggable: false,
        onTitleChange: (_k, text) => {
          currentValue = /** @type {UploadValue} */ ({ ...(/** @type {UploadValue} */ (currentValue)), title: text });
          onChange(currentValue);
        },
        onDescriptionChange: (_k, text) => {
          currentValue = /** @type {UploadValue} */ ({ ...(/** @type {UploadValue} */ (currentValue)), description: text });
          onChange(currentValue);
        },
        onRemove: () => clearTo(null),
      });
      preview.appendChild(cardHandle.el);
      cardHandle.setDisabled(session.isDisabled()); // apply current state immediately
      return;
    }

    // Plain (non-photo) mode — unchanged.
    const isImage = val.kind === 'existing'
      ? (val.url ?? '').match(/\.(png|jpe?g|gif|webp|svg)(\?|$)/i)
      : (val.file.type.startsWith('image/'));

    if (isImage) {
      const img = /** @type {HTMLImageElement} */ (createElement('img', { class: 'file-driver__img' }));
      if (val.kind === 'existing') {
        attachImageFallback(img); // one-shot fallback BEFORE src (a 404'd url else shows a broken icon)
        img.src = val.url;
      } else {
        currentObjectUrl = URL.createObjectURL(val.file);
        img.src = currentObjectUrl;
      }
      img.alt = val.kind === 'existing' ? (val.name ?? '') : val.file.name;
      preview.appendChild(img);
    } else {
      const name = val.kind === 'existing' ? (val.name ?? val.url) : val.file.name;
      const nameEl = createElement('span', { class: 'file-driver__filename' });
      nameEl.textContent = name;
      preview.appendChild(nameEl);
    }

    const removeBtn = createElement('button', { type: 'button', class: 'file-driver__remove' });
    removeBtn.textContent = '×';
    removeBtn.addEventListener('click', () => {
      if (session.isInteractionBlocked()) return;
      clearTo(null);
    });
    preview.appendChild(removeBtn);
  }

  input.addEventListener('change', () => {
    const file = input.files?.[0];
    if (!file) return;

    // Gate against accept BEFORE either commit branch — `input.accept` is only a
    // hint (drag-drop bypasses it), and the no-uploadClient branch stores the raw
    // file without uploading. Reset the input so the same file can be re-picked.
    if (!acceptMatcher(normalizeForAccept(file))) {
      input.value = '';
      log.warn('Rejected file type', { data: { name: file.name, accept } });
      showDriverError('That file type is not allowed here.');
      return;
    }
    clearDriverError();

    if (ctx.uploadClient) {
      const uploadClient = ctx.uploadClient;
      // Single-field native upload ALSO catalogues to the library when a browse
      // client with persistUpload is available (so native + modal both create a
      // /files row); else it falls back to today's URL-only commit. The session
      // owns the lifecycle/abort; `produce` does the upload(+persist)+map, gated by
      // `isCurrent()` BEFORE persist (no orphan /files row); `commit` renders.
      const persistUpload = ctx.filePicker?.browse.persistUpload;
      log.info('File upload start', { data: { name: file.name } });
      session.runNativeUpload({
        produce: async ({ signal, isCurrent }) => {
          const result = await uploadClient.upload(file, { signal });
          if (!isCurrent()) return null; // pre-persist freshness guard
          if (persistUpload) {
            const fileMeta = await persistUpload(result, file, { signal });
            // Keep the source file's name as the fallback (a nameless persist result would
            // otherwise degrade to the URL via mapFileMetaToExistingUpload).
            return /** @type {ExistingUpload} */ (
              mapFileMetaToExistingUpload({ ...fileMeta, name: fileMeta.name ?? file.name })
            );
          }
          return mapUploadResultToExistingUpload(result, file);
        },
        commit: (/** @type {ExistingUpload} */ value) => {
          log.info('File upload success', { data: { name: file.name } });
          commitValue(value);
        },
      });
    } else {
      commitValue(/** @type {NewUpload} */ ({ kind: 'new', file }));
    }
  });

  chooseBtn.addEventListener('click', () => {
    input.click();
  });

  // Render initial value
  renderPreview(currentValue);

  return {
    getValue() { return currentValue; },
    setValue(value) {
      currentValue = value != null ? /** @type {UploadValue} */ (value) : null;
      currentKey = currentValue ? nextCardKey() : ''; // fresh card identity on external set
      renderPreview(currentValue); // destroys any prior card handle first
      clearDriverError();
    },
    clearValue() {
      // Clear the inline error regardless — error state is independent of value,
      // so a no-op value-clear (empty field) must still dismiss a stale message.
      clearDriverError();
      if (currentValue === null) return;
      currentValue = null;
      currentKey = '';
      renderPreview(null); // destroys the card handle
      input.value = '';
    },
    setDisabled(disabled) {
      session.setForcedDisabled(disabled);
      if (disabled) { session.closePicker(); session.abortNativeUpload(); }
      session.applyDisabled();
    },
    setVisible(visible) {
      wrapper.hidden = !visible;
      if (!visible) { session.closePicker(); session.abortNativeUpload(); }
    },
    setErrors() {},
    setAriaInvalid(invalid) {
      wrapper.setAttribute('aria-invalid', invalid ? 'true' : 'false');
    },
    destroy() {
      session.markDestroyed();
      session.closePicker();
      session.abortNativeUpload();
      if (cardHandle) { cardHandle.destroy(); cardHandle = null; }
      if (currentObjectUrl) {
        URL.revokeObjectURL(currentObjectUrl);
        currentObjectUrl = null;
      }
      wrapper.remove();
    },
  };
}

// ---------------------------------------------------------------------------
// Multi-file mode
// ---------------------------------------------------------------------------

/**
 * @param {HTMLElement} container
 * @param {import('../../../contracts/forms.js').FileFieldDef} def
 * @param {import('../../../contracts/forms.js').FieldDriverContext} ctx
 * @returns {import('../../../contracts/forms.js').FieldDriverHandle}
 */
function mountMulti(container, def, ctx) {
  const { onChange } = ctx;
  const accept = def.accept ?? '';
  const acceptMatcher = buildAcceptMatcher(accept);
  const photoCards = def.photo === true; // render image-card metadata UI per item
  /** @type {UploadValue[]} */
  let currentValues = Array.isArray(ctx.value) ? [.../** @type {UploadValue[]} */ (ctx.value)] : [];
  /** Stable client keys, lockstep with currentValues (photo mode). Resolves callbacks by key, not identity. */
  let keys = currentValues.map(() => nextCardKey());
  /** Live photo-card handles for the current render (photo mode) — destroyed before each rebuild. */
  /** @type {ReturnType<typeof createImageCard>[]} */
  let cardHandles = [];
  /** @type {Map<UploadValue, string>} */
  const objectUrls = new Map();

  /** Regenerate the keys array lockstep with `vals` (init / setValue / clearValue). @param {UploadValue[]} vals */
  function syncKeys(vals) { keys = vals.map(() => nextCardKey()); }

  const wrapper = createElement('div', { class: 'file-driver file-driver--multi' });
  const grid = createElement('div', { class: 'file-driver__grid' });

  const hasPicker = Boolean(ctx.filePicker?.browse && ctx.filePicker?.open);
  const actions = createElement('div', { class: 'file-driver__actions' });
  // Library picker (primary) — only when the in-editor file-picker capability is
  // available in ctx.
  const libraryBtn = hasPicker
    ? createElement('button', { type: 'button', class: 'file-driver__library btn btn-primary' })
    : null;
  if (libraryBtn) libraryBtn.textContent = 'Add files';
  // Native disk upload. Redundant when the picker (with its own Upload) is wired —
  // show it ONLY as the fallback for fields without a picker. (Always created so
  // the <input>/change handler wiring below is uniform; just not appended.)
  const addBtn = createElement('button', { type: 'button', class: 'file-driver__add btn' });
  addBtn.textContent = 'Upload from computer';
  if (libraryBtn) actions.appendChild(libraryBtn);
  if (!hasPicker) actions.appendChild(addBtn);

  const input = /** @type {HTMLInputElement} */ (createElement('input', {
    type: 'file',
    class: 'file-driver__input',
    multiple: 'multiple',
  }));
  if (def.accept) input.setAttribute('accept', def.accept);
  input.style.display = 'none';

  // Shared session/abort/error machinery (see file-driver-session.js). `onDisabledChange`
  // fans disabled state into every live photo card (cards aren't in `buttons`).
  const session = createFileDriverSession({
    wrapper, def, ctx, buttons: [addBtn, libraryBtn],
    onDisabledChange: (disabled) => { for (const h of cardHandles) h.setDisabled(disabled); },
  });
  const { errorEl, showError: showDriverError, clearError: clearDriverError } = session;

  wrapper.appendChild(grid);
  wrapper.appendChild(actions);
  wrapper.appendChild(input);
  wrapper.appendChild(errorEl); // appended last — same DOM order as before
  container.appendChild(wrapper);

  async function openLibraryPicker() {
    const result = await session.openPicker({ multiple: true });
    // Multi: map ALL returned FileMeta and append (the modal can return several).
    const mapped = (result ?? [])
      .map(mapFileMetaToExistingUpload)
      .filter((/** @type {ExistingUpload | null} */ v) => v !== null);
    if (mapped.length === 0) return; // cancel / abort / stale / unusable result
    clearDriverError();
    appendValues(/** @type {ExistingUpload[]} */ (mapped));
  }
  if (libraryBtn) libraryBtn.addEventListener('click', openLibraryPicker);

  /** Append items to currentValues, minting a key per item (keeps keys lockstep). @param {UploadValue[]} items */
  function appendValues(items) {
    currentValues = [...currentValues, ...items];
    keys = [...keys, ...items.map(() => nextCardKey())];
    renderAll();
    onChange(currentValues);
  }

  /** @type {string | null} */
  let draggedKey = null;

  /** Remove the item at index `i` (keys lockstep), revoking any tracked object URL. @param {number} i */
  function removeAt(i) {
    if (i < 0 || i >= currentValues.length) return;
    const val = currentValues[i];
    const objUrl = objectUrls.get(val);
    if (objUrl) { URL.revokeObjectURL(objUrl); objectUrls.delete(val); }
    currentValues = [...currentValues.slice(0, i), ...currentValues.slice(i + 1)];
    keys = [...keys.slice(0, i), ...keys.slice(i + 1)];
    renderAll();
    clearDriverError();
    onChange(currentValues);
  }

  /**
   * Move the item at `fromIdx` to `toIdx` (permutes values AND keys). `opts.focus`
   * (set ONLY by the Move-button callbacks, never by drag/drop) re-homes keyboard
   * focus onto the moved card's Move button after the re-render — a drag must NOT
   * steal focus to a button.
   * @param {number} fromIdx @param {number} toIdx @param {{ focus?: boolean }} [opts]
   */
  function moveItem(fromIdx, toIdx, opts = {}) {
    if (fromIdx === toIdx || fromIdx < 0 || toIdx < 0) return;
    const movedKey = keys[fromIdx];
    const movingUp = toIdx < fromIdx;
    const nv = [...currentValues]; const nk = [...keys];
    const [mv] = nv.splice(fromIdx, 1); const [mk] = nk.splice(fromIdx, 1);
    nv.splice(toIdx, 0, mv); nk.splice(toIdx, 0, mk);
    currentValues = nv; keys = nk;
    renderAll();
    onChange(currentValues);
    if (opts.focus && photoCards) {
      // Look up the moved card by its rebuilt-keys index (handles don't expose key).
      const handle = cardHandles[keys.indexOf(movedKey)];
      handle?.focusMove?.(movingUp);
    }
  }

  /**
   * Build the per-item element at index `idx`. Photo mode → a `createImageCard` (tracked in
   * `cardHandles`) with drag SPLIT: the card's handle is the drag origin, the card root is the drop
   * target. Non-photo → today's name-only tile (fully draggable root).
   * @param {number} idx
   */
  function makeTile(idx) {
    const val = currentValues[idx];

    if (photoCards) {
      const card = createImageCard({
        key: keys[idx],
        value: val,
        draggable: true,
        onTitleChange: (key, text) => {
          const i = keys.indexOf(key);
          if (i === -1) return;
          currentValues = currentValues.map((v, j) => (j === i ? { ...v, title: text } : v));
          onChange(currentValues);
        },
        onDescriptionChange: (key, text) => {
          const i = keys.indexOf(key);
          if (i === -1) return;
          currentValues = currentValues.map((v, j) => (j === i ? { ...v, description: text } : v));
          onChange(currentValues);
        },
        onRemove: (key) => {
          if (session.isInteractionBlocked()) return;
          removeAt(keys.indexOf(key));
        },
        onMoveUp: (key) => {
          if (session.isInteractionBlocked()) return;
          const i = keys.indexOf(key);
          moveItem(i, i - 1, { focus: true });
        },
        onMoveDown: (key) => {
          if (session.isInteractionBlocked()) return;
          const i = keys.indexOf(key);
          moveItem(i, i + 1, { focus: true });
        },
      });
      cardHandles.push(card);
      const root = card.el;
      root.dataset.tileIdx = String(idx);
      // Drag SPLIT: start from the handle (inputs stay editable), drop anywhere on the root.
      const handle = card.handle;
      if (handle) {
        handle.addEventListener('dragstart', (e) => {
          if (session.isInteractionBlocked()) { e.preventDefault(); return; }
          draggedKey = keys[idx];
          root.classList.add('file-driver__card--dragging');
          if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
        });
        handle.addEventListener('dragend', () => {
          draggedKey = null;
          root.classList.remove('file-driver__card--dragging');
          grid.querySelectorAll('.file-driver__card--over').forEach(el => el.classList.remove('file-driver__card--over'));
        });
      }
      root.addEventListener('dragover', (e) => {
        if (draggedKey === null) return;
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
        root.classList.add('file-driver__card--over');
      });
      root.addEventListener('dragleave', () => root.classList.remove('file-driver__card--over'));
      root.addEventListener('drop', (e) => {
        e.preventDefault();
        root.classList.remove('file-driver__card--over');
        if (draggedKey === null) return;
        moveItem(keys.indexOf(draggedKey), parseInt(root.dataset.tileIdx ?? '-1', 10));
      });
      return root;
    }

    // Non-photo: today's name-only tile (unchanged behavior).
    const tile = createElement('div', { class: 'file-driver__tile', draggable: 'true', 'data-tile-idx': String(idx) });
    const nameEl = createElement('span', { class: 'file-driver__tilename' });
    nameEl.textContent = val.kind === 'existing' ? (val.name ?? val.url) : val.file.name;
    const removeBtn = createElement('button', { type: 'button', class: 'file-driver__tile-remove' });
    removeBtn.textContent = '×';
    removeBtn.addEventListener('click', () => {
      if (session.isInteractionBlocked()) return;
      removeAt(idx);
    });
    tile.appendChild(nameEl);
    tile.appendChild(removeBtn);
    tile.addEventListener('dragstart', (e) => {
      if (session.isInteractionBlocked()) { e.preventDefault(); return; }
      draggedKey = keys[idx];
      tile.classList.add('file-driver__tile--dragging');
      if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
    });
    tile.addEventListener('dragend', () => {
      draggedKey = null;
      tile.classList.remove('file-driver__tile--dragging');
      grid.querySelectorAll('.file-driver__tile--over').forEach(el => el.classList.remove('file-driver__tile--over'));
    });
    tile.addEventListener('dragover', (e) => {
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
      tile.classList.add('file-driver__tile--over');
    });
    tile.addEventListener('dragleave', () => tile.classList.remove('file-driver__tile--over'));
    tile.addEventListener('drop', (e) => {
      e.preventDefault();
      tile.classList.remove('file-driver__tile--over');
      if (draggedKey === null) return;
      moveItem(keys.indexOf(draggedKey), parseInt(tile.dataset.tileIdx ?? '-1', 10));
    });
    return tile;
  }

  function renderAll() {
    // Destroy prior photo-card handles BEFORE clearing the DOM (revokes their object URLs).
    if (cardHandles.length) { for (const h of cardHandles) h.destroy(); cardHandles = []; }
    grid.textContent = '';
    currentValues.forEach((val, i) => {
      // Non-photo `new` items track their object URL here; photo cards own theirs.
      if (!photoCards && val.kind === 'new' && !objectUrls.has(val)) {
        objectUrls.set(val, URL.createObjectURL(val.file));
      }
      grid.appendChild(makeTile(i));
    });
    if (photoCards) {
      const d = session.isDisabled();
      const last = cardHandles.length - 1;
      cardHandles.forEach((h, i) => {
        h.setMoveState(i > 0, i < last); // first can't move up; last can't move down
        h.setDisabled(d);
      });
    }
  }

  input.addEventListener('change', () => {
    const files = Array.from(input.files ?? []);
    if (!files.length) return;
    input.value = '';

    // Gate each file against accept; keep only accepted ones. If any were
    // rejected, surface the error (even when valid files commit in this event).
    const acceptedFiles = files.filter(f => acceptMatcher(normalizeForAccept(f)));
    const rejectedCount = files.length - acceptedFiles.length;
    if (rejectedCount > 0) {
      log.warn('Rejected file type(s)', { data: { rejectedCount, accept } });
      showDriverError('Some files were not allowed and were skipped.');
    } else {
      clearDriverError();
    }
    if (!acceptedFiles.length) return;

    if (ctx.uploadClient) {
      // Capture the upload client so TS retains narrowing across the async boundary.
      // ctx.uploadClient is assigned once at mount and never reassigned.
      const uploadClient = ctx.uploadClient;
      // MULTI native upload stays URL-only (NOT catalogued): a batch persist would
      // risk an unrollback-able partial failure (the editor's narrow client has no
      // remove). Catalogued multi-upload is the modal's job. The session owns the
      // lifecycle/abort; `produce` does the batch upload+map, `commit` appends+renders.
      log.info('File upload start', { data: { count: acceptedFiles.length } });
      session.runNativeUpload({
        produce: async ({ signal }) => {
          const results = await Promise.all(acceptedFiles.map(f => uploadClient.upload(f, { signal })));
          return results.map((r, i) => mapUploadResultToExistingUpload(r, acceptedFiles[i]));
        },
        commit: (/** @type {ExistingUpload[]} */ newItems) => {
          log.info('File upload success', { data: { count: newItems.length } });
          appendValues(newItems);
        },
      });
    } else {
      appendValues(acceptedFiles.map(f => /** @type {NewUpload} */ ({ kind: 'new', file: f })));
    }
  });

  addBtn.addEventListener('click', () => {
    input.click();
  });

  // Render initial values
  renderAll();

  return {
    getValue() { return currentValues; },
    setValue(value) {
      // Revoke all existing object URLs before replacing values
      for (const [, url] of objectUrls) {
        URL.revokeObjectURL(url);
      }
      objectUrls.clear();
      currentValues = Array.isArray(value) ? [.../** @type {UploadValue[]} */ (value)] : [];
      syncKeys(currentValues); // fresh keys lockstep with the new values
      renderAll(); // destroys prior card handles first
      clearDriverError();
    },
    clearValue() {
      // Error state is independent of value — clear it even when the value-clear
      // is a no-op (empty field), but don't touch currentValues (same reference).
      clearDriverError();
      if (currentValues.length === 0) return; // idempotent no-op, same reference
      for (const [, url] of objectUrls) {
        URL.revokeObjectURL(url);
      }
      objectUrls.clear();
      currentValues = [];
      keys = [];
      renderAll(); // destroys prior card handles
    },
    setDisabled(disabled) {
      session.setForcedDisabled(disabled);
      if (disabled) { session.closePicker(); session.abortNativeUpload(); }
      session.applyDisabled();
    },
    setVisible(visible) {
      wrapper.hidden = !visible;
      if (!visible) { session.closePicker(); session.abortNativeUpload(); }
    },
    setErrors() {},
    setAriaInvalid(invalid) {
      wrapper.setAttribute('aria-invalid', invalid ? 'true' : 'false');
    },
    destroy() {
      session.markDestroyed();
      session.closePicker();
      session.abortNativeUpload();
      // Destroy photo-card handles (revokes their object URLs) + any non-photo tracked URLs.
      for (const h of cardHandles) h.destroy();
      cardHandles = [];
      for (const [, url] of objectUrls) {
        URL.revokeObjectURL(url);
      }
      objectUrls.clear();
      wrapper.remove();
    },
  };
}

// ---------------------------------------------------------------------------
// Driver export
// ---------------------------------------------------------------------------

/** @type {import('../../../contracts/forms.js').FieldDriver} */
export const fileDriver = {
  type: 'file',
  mount(container, ctx) {
    const def = /** @type {import('../../../contracts/forms.js').FileFieldDef} */ (ctx.def);
    if (def.multiple) {
      return mountMulti(container, def, ctx);
    }
    return mountSingle(container, def, ctx);
  },
};
